import { useState, useRef, useEffect } from 'react';
import type { TopicCitation } from '@researcher/shared';

interface CitationPopoverProps {
  citation: TopicCitation;
  number: number;
}

export default function CitationPopover({ citation, number }: CitationPopoverProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const pubmedUrl = `https://pubmed.ncbi.nlm.nih.gov/${citation.pubmedId}/`;

  return (
    <span ref={ref} className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        className="text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 text-xs font-medium cursor-pointer"
        title={citation.title}
      >
        [{number}]
      </button>

      {open && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-3 z-50">
          <p className="text-xs font-medium text-gray-800 dark:text-gray-100 mb-1 line-clamp-2">
            {citation.title}
          </p>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-1">
            {citation.authors} &middot; {citation.journal} ({citation.year})
          </p>
          <div className="flex items-center justify-between">
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
              {citation.evidenceType.replace(/-/g, ' ')} &middot; Quality: {citation.qualityScore}/10
            </span>
            <a
              href={pubmedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] text-pink-600 dark:text-pink-400 hover:underline"
            >
              PubMed
            </a>
          </div>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-white dark:bg-gray-800 border-r border-b border-gray-200 dark:border-gray-700" />
        </div>
      )}
    </span>
  );
}
